/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars, no-shadow */
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import { DENORMALIZED_KEYS_TO_STRIP } from '../../questionnaireTemplate.constants';
import { QUESTIONNAIRE_ELEMENT_TYPE, SCREENING_TYPE } from '../../questionnaireTemplate.enums';
import { DenormalizedElement, QuestionnaireEntities, QuestionnaireOrdering } from '../../questionnaireTemplate.types';

/**
 * Recursively removes empty values from an object or array.
 * Prunes: null, undefined, [], and {}
 */
export const pruneEmpty = (obj: any): any => {
  if (isArray(obj)) {
    return obj.map(pruneEmpty).filter((v) => !isNil(v) && (!isObject(v) || Object.keys(v).length > 0));
  }

  if (obj !== null && isObject(obj)) {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([k, v]) => [k, pruneEmpty(v)])
        .filter(([_, v]) => !isNil(v) && !(isArray(v) && v.length === 0) && !(isObject(v) && Object.keys(v).length === 0)),
    );
  }

  return obj;
};

/**
 * Recursively denormalizes a questionnaire structure
 * @param ids - Array of element IDs to process at this level
 * @param entities - Normalized entities object containing all form elements
 * @param ordering - Object defining parent-child relationships and branch ordering
 * @param parentId - ID of the parent element (null for root elements)
 * @param optionId - ID of the option that triggered this branch (null for root)
 * @returns Array of denormalized elements with nested followups
 */
export const denormalizeQuestionnaire = (
  ids: string[],
  entities: QuestionnaireEntities,
  ordering: QuestionnaireOrdering,
  parentId: string | null = null,
  optionId: string | null = null,
): DenormalizedElement[] => {
  const result = ids
    .map((id): DenormalizedElement | null => {
      const element = entities.elements[id];
      if (!element) return null;

      const { ...elementCopy } = element;
      const cleanElement = { ...elementCopy } as any;
      DENORMALIZED_KEYS_TO_STRIP.forEach((key) => delete cleanElement[key]);

      const currentOptions = element.options ? [...element.options] : [];

      // Logic for syncing options with active branches...
      if (element.activeBranches) {
        element.activeBranches.forEach((branchName) => {
          const exists = currentOptions.some((opt) => opt.text?.toLowerCase() === branchName.toLowerCase());
          if (!exists) {
            currentOptions.push({
              id: `${id}-${branchName}`,
              text: branchName,
            });
          }
        });
      }

      const followups: DenormalizedElement[] = [];
      if (element.activeBranches) {
        element.activeBranches.forEach((branchName) => {
          const branchKey = `${id}-${branchName}`;
          const branchChildrenIds = ordering[branchKey];
          if (branchChildrenIds?.length) {
            const matchingOption = currentOptions.find((opt) => opt.text?.toLowerCase() === branchName.toLowerCase());
            const children = denormalizeQuestionnaire(
              branchChildrenIds,
              entities,
              ordering,
              id,
              matchingOption?.id || /* istanbul ignore next */ null,
            );
            followups.push(...children);
          }
        });
      }

      // Step 3: Construct the raw object
      let rawElement = {
        ...cleanElement,
        options: currentOptions,
        parent: parentId,
        optionId,
        followups,
        knockout: element.screeningType === SCREENING_TYPE.KNOKOUT,
        isPausable: element.screeningType === SCREENING_TYPE.REVIEW,
        questionOrder: ids.indexOf(id) + 1,
      };

      if (
        element.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO ||
        element.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE
      ) {
        rawElement = {
          ...rawElement,
          preferredAnswer: element.preferredAnswer === 'yes',
        };
      }

      if (
        element.questionType === QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE ||
        element.questionType === QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE_WITH_IMAGE
      ) {
        const currentOptions = element.options
          ? element.options.map((opt) => {
              const { isPreferredAnswer, ...rest } = opt;
              return rest;
            })
          : [];
        rawElement = {
          ...rawElement,
          preferredAnswers: element.options?.map((option) => (option?.isPreferredAnswer ? option.id : null))?.filter(Boolean),
          options: currentOptions,
        };
      }

      // Step 4: PRUNE the element before returning
      return pruneEmpty(rawElement);
    })
    .filter((item): item is DenormalizedElement => item !== null);

  return result;
};
